import type { Request } from 'effect';
import { Effect } from 'effect';

/**
 * v4 replacement for the v3 `RequestResolver.contextFromServices(...)` helper.
 *
 * In v4, a `RequestResolver.fromEffect` callback receives a `Request.Entry<T>` (not the
 * unwrapped request) and must return an `Effect<Success, Error>` with `R = never`.
 * Services flow through `entry.context: Context.Context<Request.Services<T>>`, derived
 * from the 3rd type parameter on `Request.Request<A, E, R>`.
 *
 * `liftRequest` adapts a request-shaped fetcher fn to the entry-shaped resolver fn,
 * provisioning services from `entry.context`.
 */
export const liftRequest =
  <T extends Request.Any>(
    f: (request: T) => Effect.Effect<Request.Success<T>, Request.Error<T>, Request.Services<T>>
  ) =>
  (entry: Request.Entry<T>): Effect.Effect<Request.Success<T>, Request.Error<T>> =>
    f(entry.request).pipe(Effect.provideContext(entry.context));
